/* eslint func-names: off */
import pick from 'lodash/pick'
import { v4 as uuid } from 'uuid'
import * as Yup from 'yup'
import { CustomFieldPath } from '~/custom_reports/types'
import { filterOperators } from '~/custom_reports/sharedV3'
import { lazyMsg } from '~/common/localizationUtils'
import { customReportsApi } from './api'

// maps form values to query
export const mapToQuery = ({ query }) => {
  const base_dataset = query.fieldSources?.filter(source => source.type === 'dataset')[0] || {}
  return {
    dataset: base_dataset.dataset_id,
    calculatedFields: query.calculatedFields,
    parameterFields: query.parameterFields,
    joins: query.fieldSources
      ?.filter(source => source.type === 'join')
      .map(fs => pick(fs, ['source', 'target', 'id', 'path'])),
    fields: query.fields,
    filters: query.filters,
    orderBy: query.orderBy,
    apply_distinct: query.apply_distinct ? query.apply_distinct : false
  }
}

// maps form values to save report model
export const mapToSavedReport = ({ query, report, validate_accessible_query, report_type, owner_type }) => ({
  query: mapToQuery({ query }),
  name: report.name,
  description: report.description,
  section_name: report.section_name,
  config_export_enabled: report.config_export_enabled,
  id: report.id,
  report_type: report.report_type ?? report_type,
  owner_id: report.owner?.value,
  owner_type: report.owner?.type ?? owner_type,
  validate_accessible_query
})

export const reportDefinitionInitialValues = () => ({
  id: null,
  name: '',
  description: '',
  config_export_enabled: false,
  report_type: 'saved',
  is_favorite: false
})

export const getFloatingReportDescription = title => `Tabular data used to generate the '${title}' chart`

export const initializeFormValues = (idOrUuid, selectedVersion, type, setter, callback, dashboardProps) => {
  if (dashboardProps) {
    const values = {
      query: dashboardProps.query,
      report: {
        ...reportDefinitionInitialValues(),
        name: dashboardProps.title,
        description: getFloatingReportDescription(dashboardProps.title)
      }
    }
    setter(values)
    callback(values)
  } else if (!idOrUuid) {
    setter({
      query: {
        dataset: null,
        joins: [],
        fields: [],
        filters: [],
        orderBy: [],
        calculatedFields: [],
        parameterFields: []
      },
      report: reportDefinitionInitialValues()
    })
  } else {
    const getReport = customReportsApi.getSavedReport(idOrUuid, selectedVersion)
    getReport
      .then(report => {
        const values = {
          report: {
            report_type: report.report_type,
            id: report.id,
            uuid: report.uuid,
            name: report.name,
            description: report.description,
            section_name: report.section_name,
            creator_name: report.creator_name,
            created_by: report.created_by,
            // Report Owner dropdown expects the owner value to be in this format to populate the dropdown
            owner: { value: report.owner.id, label: report.owner.name ?? '', type: report.owner.type },
            created_at: report.created_at,
            config_export_enabled: report.config_export_enabled,
            share_settings: report.share_settings,
            selected_version: report.selected_version,
            current_version_name: report.current_version_name,
            is_favorite: report.is_favorite,
            export_hyperlink_enabled: report.export_hyperlink_enabled,
            domain: report.domain
          },
          query: {
            dataset: report.query.dataset,
            joins: report.query.joins || [],
            fields: report.query.fields.map(f => ({ ...f, id: f.id || uuid() })),
            filters: report.query.filters || [],
            orderBy: report.query.orderBy || [],
            calculatedFields: report.query.calculatedFields || [],
            parameterFields: report.query.parameterFields || []
          }
        }
        setter(values)
        callback(values)
      })
      .catch(error => {
        if (error.body?.detail === 'Not found.') setter({ report: { not_found: true } })
        else callback({ error })
      })
  }
}

export const fieldPathKeys = Object.freeze({
  calculated: '__calculated__',
  parameter: '__parameter__',
  value: '__value__'
})

const AGGREGATIONS = [
  { label: '', value: null },
  { label: lazyMsg('custom_report.builder.menu.aggregate.average.label'), value: 'avg' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.count.label'), value: 'count' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.count_distinct.label'), value: 'count_distinct' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.count_null.label'), value: 'count_null' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.count_not_null.label'), value: 'count_not_null' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.sum.label'), value: 'sum' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.min.label'), value: 'min' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.max.label'), value: 'max' },
  { label: lazyMsg('custom_report.builder.menu.aggregate.concatenate.label'), value: 'string_agg' }
]

export const getAggregateOptions = allowed =>
  AGGREGATIONS.filter(agg => !agg.value || allowed.indexOf(agg.value) !== -1)

export const isCalculatedField = field => field.type === 'calculated' || field.path === CustomFieldPath.Calculated
export const isParameterField = field => field.path === CustomFieldPath.Parameter
export const isValueField = field => field.path === CustomFieldPath.Value
export const isSystemVariableField = field => field.path === CustomFieldPath.SystemVariable
export const isDatasetField = field =>
  field.path &&
  !isCalculatedField(field) &&
  !isParameterField(field) &&
  !isValueField(field) &&
  !isSystemVariableField(field)

const isFieldValid = (datasets, field) => {
  if (isCalculatedField(field) || isParameterField(field) || isValueField(field) || isSystemVariableField(field)) {
    return true
  }
  if (!field.path) return false

  const dataset_path = field.path.split('__')
  const dataset_id = dataset_path[dataset_path.length - 1]
  const dataset = datasets.find(ds => ds.id === dataset_id)
  if (!dataset) return false
  return dataset.fields.some(datasetField => datasetField.id === field.field)
}

const getQueryValidations = (datasets, msg) =>
  Yup.object().shape({
    fields: Yup.array()
      .min(1)
      .of(
        Yup.object().shape({
          field: Yup.string()
            .required(msg('common.ui.field_required'))
            .nullable()
            .test('valid-field', msg('common.ui.field_required'), function () {
              return isFieldValid(datasets, this.parent)
            })
        })
      )
      .required(msg('common.ui.at_least_one_display_field_required')),
    filters: Yup.array().of(
      Yup.object().shape({
        field: Yup.string()
          .required(msg('common.ui.field_required'))
          .nullable()
          .test('valid-field', msg('common.ui.field_required'), function () {
            return isFieldValid(datasets, this.parent)
          }),
        operator: Yup.string().required(msg('common.ui.operator_is_required')).nullable(),
        value: Yup.string()
          .when('operator', {
            is: operator => {
              return (
                filterOperators(msg).find(o => o.value === operator) &&
                !filterOperators(msg).find(o => o.value === operator).dynamic
              )
            },
            then: Yup.string().required(msg('common.ui.value_is_required')).nullable()
          })
          .nullable()
      })
    ),
    orderBy: Yup.array().of(
      Yup.object().shape({
        path: Yup.string().required(msg('common.ui.dataset_is_required')).nullable(),
        field: Yup.string()
          .required(msg('common.ui.field_required'))
          .nullable()
          .test('valid-field', msg('common.ui.field_required'), function () {
            return isFieldValid(datasets, this.parent)
          })
      })
    )
  })

const reportDefinitionValidations = msg =>
  Yup.object().shape({
    name: Yup.string().required(msg('common.ui.name_is_required')).nullable()
  })

export const getValidationSchema = (saveIntent, datasets, msg) => {
  const schemaValidations = {
    query: getQueryValidations(datasets, msg)
  }

  if (saveIntent) {
    schemaValidations.report = reportDefinitionValidations(msg)
  }
  return Yup.object().shape(schemaValidations)
}
